import { Injectable } from '@angular/core';
import { differenceInDays, format } from 'date-fns';
import { gungComparatorHelper } from 'gung-common';
import {
  ConfigBaseFilter,
  ConfigService,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { EdiInvoiceCardListComponent } from '../../components/edi/edi-invoice-card-list/edi-invoice-card-list.component';
import { EdiInvoice, EdiOrder } from '../../models/edi';
import { BaseViewConfigService } from '../base-view-config/base-view-config.service';
import { EdiService } from '../edi/edi.service';
import { EdiMinDateConfigService } from '../edi-min-date-config.service';

export class EdiInvoiceStatusFilter extends SimpleConfigBaseFilter<EdiInvoice> {
  constructor() {
    super();
  }
  getName(): string {
    return 'INVOICE_STATUS';
  }
  getOptionIds(item: EdiInvoice): string[] {
    return [item.gungEdiMessageSendStatus || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

export class EdiInvoiceValidationFilter extends SimpleConfigBaseFilter<EdiInvoice> {
  constructor() {
    super();
  }
  getName(): string {
    return 'VALIDATION_STATE';
  }
  getOptionIds(item: EdiInvoice): string[] {
    return [item.validationState || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

export class EdiInvoiceConfigIdFilter extends SimpleConfigBaseFilter<EdiInvoice> {
  constructor() {
    super();
  }
  getName(): string {
    return 'CONFIG_ID';
  }
  getOptionIds(item: EdiInvoice): string[] {
    return [item.ediConfigId || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

export class EdiInvoiceDateFilter extends SimpleConfigBaseFilter<EdiInvoice> {
  constructor() {
    super();
  }
  getName(): string {
    return 'INVOICE_DATE';
  }
  getOptionIds(item: EdiInvoice): string[] {
    let date = item.invoiceDueDate?new Date(item.invoiceDueDate):new Date()
    return [format(date, 'yyyy-MM-dd')];
  }
  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EdiInvoicesListConfigService implements EdiMinDateConfigService<EdiInvoice> {
  protected minDateSubject = new BehaviorSubject<string>(this.getDefaultMinDate());

  constructor(protected ediService: EdiService, protected baseViewConfigService: BaseViewConfigService) {}

  getDefaultMinDate(): string {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return format(date, 'yyyy-MM-dd');
  }

  setMinDate(minDate: string): void {
    this.minDateSubject.next(minDate);
  }

  getMinDate(): string {
    return this.minDateSubject.getValue();
  }

  getItems(): Observable<EdiInvoice[]> {
    return this.minDateSubject.pipe(
      switchMap(minDate => this.ediService.getInvoices(minDate)),
      map(items => items.sort((a, b) => gungComparatorHelper([a.timestamp, a.id], [b.timestamp, b.id], -1)))
    );
  }

  getFilters(): ConfigBaseFilter<EdiInvoice>[] {
    return [new EdiInvoiceValidationFilter(), new EdiInvoiceStatusFilter(), new EdiInvoiceConfigIdFilter(), new EdiInvoiceDateFilter()];
  }

  getSortOptions(): ListSortOption<EdiInvoice>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<EdiInvoice>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => this.baseViewConfigService.getEdiInvoiceCardListComponent(),
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Edi Invoices grid'
      }
    ];
  }

  getSearchTerms(item: EdiInvoice): string[] {
    const generalSearchTerms = [
      item.documentReferences?.supplierOrder?.id,
      item.documentReferences?.buyerOrder?.id,
      item.documentReferences?.invoice?.id,
      item.documentReferences?.invoiceOcr?.id,
      item.documentReferences?.despatchAdvice?.id,
      item.documentReferences?.parcel?.id
    ].filter(s => !!s);
    if (item.erpIds) {
      return [item.id, item.consigneeGln?.toString(), item.buyerGln?.toString()]
        .concat(item.erpIds)
        .concat(generalSearchTerms);
    }
    return [item.id, item.buyerGln?.toString(), item.consigneeGln?.toString()].concat(generalSearchTerms);
  }

  getItemId(item: EdiInvoice): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}
